import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { FormFieldLabel } from '@/common/forms/FormInput';
import { InfoSectionContainer } from '@/common/InfoSection';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TabsBar } from '@/common/TabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import {
  ReassignConfirmModal,
  SKIP_REASON_KEYS,
} from '@/groups/coordinator/ReassignConfirmModal';
import { useAdminTabs } from '@/groups/coordinator/useAdminTabs';
import { PageContent } from '@/settings/settingsStyles';
import {
  PEOPLE_PICKER_DEBOUNCE_MS,
  PEOPLE_PICKER_MIN_QUERY,
  PEOPLE_PICKER_PAGE_SIZE,
  type Person,
  useAsyncPeoplePicker,
} from '~/hooks/useAsyncPeoplePicker';
import { SearchSelect } from '~/styles/forms/SearchSelect';
import { GroupHeaderWrapper } from '~/styles/layoutElements';
import { formatString } from '~/utils/stringFormat';

type ReassignPreview = components['schemas']['ReassignPreview'];
type ReassignStudentsRequest = components['schemas']['ReassignStudentsRequest'];
type ReassignStudentsResult = components['schemas']['ReassignStudentsResult'];

/**
 * The success banner's singular and plural forms, as two keys chosen on the
 * count: the locale files carry no i18next plural keys.
 */
const COUNT_KEYS = {
  one: 'groups.d-student-reassigned-successfully',
  other: 'groups.d-students-reassigned-successfully',
} as const;

/**
 * `forbidden` is the refusal a user can reach: the `/admin/*` guard admits the
 * admin of any group type, so a study group's admin gets this screen and only
 * the two POSTs refuse. Anything else, including the 400
 * `reassign_target_not_in_group`, which the picker can't produce, falls to the
 * generic message.
 *
 * The title alone reads as a heading with no sentence, so the generic
 * permission body goes under it; `groups.tag-permission-denied-body` is
 * specific to member tags.
 */
const FAILURE_COPY = {
  forbidden: {
    title: 'groups.tag-permission-denied-title',
    body: 'wbs.you-dont-have-permission',
  },
} as const;

/**
 * Options are labelled "Name (#id)", as in legacy: duplicate display names
 * occur among both students and helpers, so the id is what tells them apart.
 */
const pickerLabel = (displayName: string, id: number): string =>
  `${displayName} (#${id})`;

const noop = (): void => {};

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin/reassign-students',
)({
  component: ReassignStudentsScreen,
  // Only a positive integer engages the deep-link branch. A junk or fractional
  // `?reassign=` would otherwise lock the picker on an id the student read
  // 422s, leaving an empty locked field that refuses every submit, and
  // `?reassign=0` would preselect a locked "Deleted User".
  validateSearch: (search): { reassign?: number } => {
    const id = Number(search.reassign);
    return { reassign: Number.isInteger(id) && id > 0 ? id : undefined };
  },
});

function ReassignStudentsScreen() {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const { reassign } = Route.useSearch();
  const navigate = useNavigate();
  const adminTabs = useAdminTabs();

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id ?? 0;

  // What the person is typing, and what the query is keyed on. Both pickers
  // debounce as legacy's select2 does (250ms).
  const [studentTerm, setStudentTerm] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [helperTerm, setHelperTerm] = useState('');
  const [helperSearch, setHelperSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(
      () => setStudentSearch(studentTerm),
      PEOPLE_PICKER_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [studentTerm]);

  useEffect(() => {
    const timer = setTimeout(
      () => setHelperSearch(helperTerm),
      PEOPLE_PICKER_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [helperTerm]);

  const { data: studentMatches, isFetching: studentsFetching } =
    api.getGroupStudents.useQuery(
      {
        path: { group_id: groupId },
        query: { search: studentSearch, limit: PEOPLE_PICKER_PAGE_SIZE },
      },
      {
        enabled: groupId > 0 && studentSearch.length >= PEOPLE_PICKER_MIN_QUERY,
      },
    );

  const { data: helperMatches, isFetching: helpersFetching } =
    api.getGroupStudyHelpers.useQuery(
      {
        path: { group_id: groupId },
        query: { search: helperSearch, limit: PEOPLE_PICKER_PAGE_SIZE },
      },
      {
        enabled: groupId > 0 && helperSearch.length >= PEOPLE_PICKER_MIN_QUERY,
      },
    );

  const studentResults = useMemo<Person[]>(
    () =>
      (studentMatches?.data ?? []).map((student) => ({
        id: student.id,
        name: pickerLabel(student.display_name, student.id),
      })),
    [studentMatches],
  );

  // The target must be an admin or mod of the group, as in legacy. Filtered
  // client-side rather than by the endpoint; it can't produce a wrong write,
  // because the server refuses a non-leader target.
  const helperResults = useMemo<Person[]>(
    () =>
      (helperMatches?.data ?? [])
        .filter((member) => member.role === 'admin' || member.role === 'mod')
        .map((member) => ({
          id: member.id,
          name: pickerLabel(member.display_name, member.id),
        })),
    [helperMatches],
  );

  // The control stays in its loading state through the debounce window too, so
  // it does not flash "no results" between a keystroke and the request it
  // caused.
  const studentPicker = useAsyncPeoplePicker(
    {
      results: studentResults,
      loading:
        studentTerm.length >= PEOPLE_PICKER_MIN_QUERY &&
        (studentTerm !== studentSearch || studentsFetching),
      onSearch: setStudentTerm,
    },
    true,
  );
  const helperPicker = useAsyncPeoplePicker(
    {
      results: helperResults,
      loading:
        helperTerm.length >= PEOPLE_PICKER_MIN_QUERY &&
        (helperTerm !== helperSearch || helpersFetching),
      onSearch: setHelperTerm,
    },
    false,
  );

  // `?reassign=<id>`, from the congregation Students Waiting widget and table.
  // Resolved through the picker's own endpoint, so an id from another
  // congregation resolves to nothing instead of leaking a name.
  const { data: deepLinked } = api.getGroupStudents.useQuery(
    { path: { group_id: groupId }, query: { student_id: reassign } },
    { enabled: reassign != null && groupId > 0 },
  );

  const { preselect: preselectStudent } = studentPicker;
  // Applied once per deep-linked id: a successful write clears both pickers and
  // invalidates `getGroupStudents`, and without this the refetch would put the
  // just-moved student straight back into the (disabled) field.
  const appliedDeepLink = useRef<number | null>(null);

  useEffect(() => {
    if (reassign == null || deepLinked === undefined) return;
    if (appliedDeepLink.current === reassign) return;
    appliedDeepLink.current = reassign;
    const row = deepLinked.data[0];
    preselectStudent(
      row
        ? { id: row.id, name: pickerLabel(row.display_name, row.id) }
        : // Legacy renders `Deleted User` for an id it can't resolve.
          // The id suffix is kept so the locked field names the id the link
          // carried.
          {
            id: reassign,
            name: pickerLabel(t('actions.deleted-user'), reassign),
          },
    );
  }, [reassign, deepLinked, preselectStudent, t]);

  const [confirming, setConfirming] = useState<{
    body: ReassignStudentsRequest;
    preview: ReassignPreview;
  } | null>(null);
  const [result, setResult] = useState<ReassignStudentsResult | null>(null);
  const [selectionInvalid, setSelectionInvalid] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const { mutateAsync: previewReassign, isPending: isPreviewing } =
    api.previewGroupStudentsReassign.useMutation();
  const { mutateAsync: reassignStudents, isPending: isReassigning } =
    api.reassignGroupStudents.useMutation();

  const handleSave = async (): Promise<void> => {
    setFailure(null);
    setResult(null);
    const studentIds = studentPicker.value.map(Number);
    const helperValue = helperPicker.value[0];
    if (studentIds.length === 0 || helperValue === undefined) {
      setSelectionInvalid(true);
      return;
    }
    setSelectionInvalid(false);
    // The same body goes to both operations, as legacy posts the same form
    // twice. The write re-checks every id and applies the eligible subset, so a
    // student who became ineligible between Save and Confirm comes back in
    // `skipped[]`.
    const body: ReassignStudentsRequest = {
      student_ids: studentIds,
      study_helper_id: Number(helperValue),
    };
    try {
      const preview = await previewReassign({
        path: { group_id: groupId },
        body,
      });
      setConfirming({ body, preview });
    } catch (error) {
      setFailure(error);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (confirming === null) return;
    setFailure(null);
    try {
      const written = await reassignStudents({
        path: { group_id: groupId },
        body: confirming.body,
      });
      setConfirming(null);
      setResult(written);
      studentPicker.onChange([]);
      helperPicker.onChange([]);
      await Promise.all([
        api.getGroupStudents.invalidateQueries(),
        // The Members table's `students_adopted` counts read the column this
        // write moves, so they'd be stale for both helpers.
        api.getGroupStudyHelpers.invalidateQueries(),
      ]);
    } catch (error) {
      setConfirming(null);
      setFailure(error);
    }
  };

  const failureCode = failure !== null ? errorCode(failure) : undefined;
  const failureCopy =
    failureCode !== undefined && failureCode in FAILURE_COPY
      ? FAILURE_COPY[failureCode as keyof typeof FAILURE_COPY]
      : undefined;

  return (
    <PageContent>
      <TabsBar tabs={adminTabs} base="/groups/$groupName/admin" />
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('lessons.reassign-students')}
          placement="group_settings"
        />
      </GroupHeaderWrapper>
      {/* Legacy's message here is capitalised; this reuses the lowercase
         catalogue string, which is translated in every language. */}
      {selectionInvalid && (
        <AlertBox variant="error">
          {t('lessons.please-select-at-least')}
        </AlertBox>
      )}
      {failure !== null && (
        <AlertBox variant="error">
          <div>
            <strong>
              {failureCopy
                ? t(failureCopy.title)
                : t('wbs.something-went-wrong-please')}
            </strong>
            {failureCopy && <div>{t(failureCopy.body)}</div>}
          </div>
        </AlertBox>
      )}
      {result !== null && (
        <AlertBox variant="success">
          <div>
            {/* The count excludes skipped students, as in legacy, so it
               isn't the length of the selection. */}
            <strong>
              {formatString(
                t(result.reassigned === 1 ? COUNT_KEYS.one : COUNT_KEYS.other),
                String(result.reassigned),
              )}
            </strong>
            {/* Legacy doesn't report skips. This is the only place a
               write-time skip shows up. */}
            {result.skipped.length > 0 && (
              <SkippedList>
                {result.skipped.map((skipped) => (
                  <li key={skipped.student_id}>
                    {skipped.display_name || t('actions.deleted-user')} —{' '}
                    {t(SKIP_REASON_KEYS[skipped.reason])}
                  </li>
                ))}
              </SkippedList>
            )}
          </div>
        </AlertBox>
      )}
      <InfoBox>
        <LargeArrowBackButton
          onClick={() =>
            navigate({
              to: '/groups/$groupName/admin/group-avatar/',
              params: { groupName },
            })
          }
        />
        <Field>
          <FormFieldLabel>{t('lessons.students')}</FormFieldLabel>
          {/* Locked on a deep link, as legacy renders the select disabled.
             `onChange` is neutralised too, because `SearchSelect` still
             renders each chip's remove button while disabled. */}
          <SearchSelect
            multi
            disabled={reassign != null}
            loading={studentPicker.loading}
            minQueryLength={PEOPLE_PICKER_MIN_QUERY}
            options={studentPicker.options}
            value={studentPicker.value}
            onChange={reassign != null ? noop : studentPicker.onChange}
            onSearch={studentPicker.onSearch}
          />
        </Field>
        <Field>
          <FormFieldLabel>{t('lessons.study-helper')}</FormFieldLabel>
          <SearchSelect
            multi={false}
            loading={helperPicker.loading}
            minQueryLength={PEOPLE_PICKER_MIN_QUERY}
            options={helperPicker.options}
            value={helperPicker.value}
            onChange={helperPicker.onChange}
            onSearch={helperPicker.onSearch}
          />
        </Field>
        <Footer>
          <WBSButton
            type="button"
            wide
            isLoading={isPreviewing}
            onClick={() => {
              void handleSave();
            }}
          >
            {t('actions.save')}
          </WBSButton>
        </Footer>
      </InfoBox>
      {confirming !== null && (
        <ReassignConfirmModal
          preview={confirming.preview}
          isConfirming={isReassigning}
          onConfirm={() => {
            void handleConfirm();
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
    </PageContent>
  );
}

const InfoBox = styled(InfoSectionContainer)`
  position: relative;
`;

const Field = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const SkippedList = styled.ul`
  margin: 8px 0 0 0;
  padding-left: 20px;
`;
